import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["video", "status", "manualInput", "startButton", "stopButton"]
  static values = { submitUrl: String }

  connect() {
    this.stream = null
    this.detector = null
    this.scanning = false
    this.lastToken = null
  }

  disconnect() {
    this.stopCamera()
  }

  async startCamera() {
    if (!("BarcodeDetector" in window)) {
      this.setStatus("Tu navegador no soporta escaneo automático. Usa el token manual.", "text-amber-300")
      return
    }

    try {
      this.detector = new window.BarcodeDetector({ formats: ["qr_code"] })
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false
      })
      this.videoTarget.srcObject = this.stream
      await this.videoTarget.play()
      this.scanning = true
      this.startButtonTarget.disabled = true
      this.stopButtonTarget.disabled = false
      this.setStatus("Escaneando QR...", "text-emerald-300")
      this.scanLoop()
    } catch (error) {
      this.setStatus("No se pudo activar la cámara. Revisa permisos o usa token manual.", "text-red-300")
    }
  }

  stopCamera() {
    this.scanning = false
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop())
      this.stream = null
    }
    if (this.videoTarget) {
      this.videoTarget.pause()
      this.videoTarget.srcObject = null
    }
    if (this.hasStartButtonTarget) this.startButtonTarget.disabled = false
    if (this.hasStopButtonTarget) this.stopButtonTarget.disabled = true
    this.setStatus("Cámara inactiva.", "text-gray-400")
  }

  async scanLoop() {
    if (!this.scanning || !this.detector) return

    try {
      const barcodes = await this.detector.detect(this.videoTarget)
      if (barcodes.length > 0) {
        const token = barcodes[0].rawValue?.trim()
        if (token && token !== this.lastToken) {
          this.lastToken = token
          await this.submitToken(token)
        }
      }
    } catch (_error) {
      this.setStatus("Error leyendo QR. Intenta de nuevo.", "text-amber-300")
    }

    if (this.scanning) {
      requestAnimationFrame(() => this.scanLoop())
    }
  }

  async submitManualToken() {
    const token = this.manualInputTarget.value.trim()
    if (!token) {
      this.setStatus("Pega un token antes de confirmar.", "text-amber-300")
      return
    }

    await this.submitToken(token)
  }

  async submitToken(token) {
    this.setStatus("Confirmando asistencia...", "text-indigo-300")
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content

    const response = await fetch(this.submitUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      },
      body: JSON.stringify({ token })
    })

    const streamContent = await response.text()
    Turbo.renderStreamMessage(streamContent)

    if (response.ok) {
      this.setStatus("Check-in procesado.", "text-emerald-300")
      if (this.hasManualInputTarget) this.manualInputTarget.value = ""
    } else {
      this.setStatus("No se pudo procesar el QR.", "text-red-300")
    }
  }

  setStatus(message, colorClass) {
    this.statusTarget.textContent = message
    this.statusTarget.classList.remove("text-gray-400", "text-amber-300", "text-emerald-300", "text-red-300", "text-indigo-300")
    this.statusTarget.classList.add(colorClass)
  }
}
